import { getFullName, validateName } from "../../utils/Roles";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import {
  AttachmentType,
  CleanupFunction,
  CompanyRoleIdWithName,
  DbotEvent,
  DbotEventDispatcher,
  DecisionItem,
  DecisionTextModel,
  deleteAttachmentEvent,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  GeneralMeetingParticipation,
  isLanguageChanged,
  mapDecisionTextModel,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  mapLocalizedDecisionTextsToModel,
  PowerOfAttorneyForGeneralMeeting,
  ProjectContext,
  ProjectDocumentStatus,
  RegisterNewSignerEvent,
  registerNewSignerEvent,
  RegisterNewSignerResultEvent,
  UpdateMinutesLiveParticipationEvent,
  uploadAttachmentsEvent,
  WithAttachmentSupport,
  WithInitialization,
} from "../common/commonDomain";
import {
  calculateAllVotes,
  defaultVotes,
  GeneralMeetingForm,
  GeneralMeetingIndividualVote,
  GeneralMeetingMinutes,
  GeneralMeetingType,
  GeneralMeetingVotes,
  GeneralMeetingVoting,
  GeneralMeetingVotingType,
  getCEO,
  getChairperson,
  IndividualVote,
  individualVotes,
  liveVotes,
  mapRoleToCompanyRoleIdWithName,
  notVoted,
  ShareholderParticipation,
  unanimous,
  voted,
  VotingModeOption,
  votingOptionsMappings,
} from "../common/commonGeneralMeetingDomain";
import { newUuid } from "../../utils/uuid";
import { DocumentLanguage, isOk, LocalizedText } from "../../common/common";
import { FileUploadConfig, UploadedAttachment } from "../../fileUpload/fileUploadDomain";
import { filterDecisionRequiringVoting } from "../freetext/freetextDomain";
import { ExtendedShareholderDTO, ShareholderData } from "../../types/Shareholder";
import { defaultFilterFn } from "../../components-v2/Autocomplete/autocompleteHelpers";
import {
  downloadSignedDocumentAction,
  openSendForReviewDialogAction,
  openSendForSigningDialogAction,
} from "../summary/summaryDomain";
import { formatDateAndTime } from "../../i18n/dateFormat";
import { formatPercent } from "../../utils/numbers";
import {
  LiveVotingResults,
  VotingInputData,
} from "../../meetings/generalMeeting/electronicGeneralMeetingDomain";
import { GeneralMeetingVoteType } from "../../meetings/generalMeeting/generalMeetingDomain";
import { hasShares } from "../../stores/ShareholdersStore";
import { CompanyRole } from "../../types/CompanyInfo";
import { compact, sumBy } from "lodash";
import { TMessage } from "../../i18n/tt";
import { DocumentType } from "../../common/documents";
import {
  createEmailField,
  NumberFieldModel,
  RadioFieldModel,
  requiredOfficialDocumentLanguageFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import Big from "big.js";
import { ShareType, ShareTypeCode } from "../../common/share";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";
import { isExtensionPdf } from "../../utils/files";

export type SelectedSigners = {
  meetingChair: CompanyRoleIdWithName | null;
  coSigner: CompanyRoleIdWithName | null;
};

export type RepresentativeOption = {
  name: string;
  companyRoleId?: number;
};

type LiveVotingData = {
  votesFor: number;
  votesAgainst: number;
  shareCapitalFor: number;
  shareCapitalAgainst: number;
};

type IndividualVotingData = {
  votesFor: number;
  votesAgainst: number;
  votesBlank: number;
  shareCapitalFor: number;
  shareCapitalAgainst: number;
  shareCapitalBlank: number;
};

export class GeneralMeetingVotingModel {
  type: GeneralMeetingVoting["type"];
  votingType?: GeneralMeetingVotingType;
  votesFor: NumberFieldModel;
  votesAgainst: NumberFieldModel;
  votesBlank: NumberFieldModel;
  representedVotes = 0;
  representedShareCapital = 0;
  shareCapitalFor: NumberFieldModel;
  shareCapitalAgainst: NumberFieldModel;
  shareCapitalBlank: NumberFieldModel;
  selectedVotingMode?: VotingModeOption;
  liveVoting?: LiveVotingData;
  individualVoting?: IndividualVotingData;
  votingInput?: VotingInputData;

  newSelectedVotingType?: GeneralMeetingVotingType;

  constructor(
    data: GeneralMeetingVoting,
    votingInput?: VotingInputData,
    votingType?: GeneralMeetingVotingType,
  ) {
    this.type = data.type;
    this.votingType = votingType;
    this.votesFor = new NumberFieldModel({
      value: data.type === "voted" ? data.votesFor : 0,
      isPositiveOrZero: true,
      onChange: (votes) =>
        this.isSameVotingWeight
          ? this.shareCapitalFor.setAndCommitInputValue(this.calculateShareCapital(votes))
          : undefined,
    });
    this.votesAgainst = new NumberFieldModel({
      value: data.type === "voted" ? data.votesAgainst : 0,
      isPositiveOrZero: true,
      onChange: (votes) =>
        this.isSameVotingWeight
          ? this.shareCapitalAgainst.setAndCommitInputValue(this.calculateShareCapital(votes))
          : undefined,
    });
    this.votesBlank = new NumberFieldModel({
      value: data.type === "voted" ? data.votesBlank : 0,
      isPositiveOrZero: true,
      onChange: (votes) =>
        this.isSameVotingWeight
          ? this.shareCapitalBlank.setAndCommitInputValue(this.calculateShareCapital(votes))
          : undefined,
    });

    this.shareCapitalFor = new NumberFieldModel({
      value: data.type === "voted" ? data.shareCapitalFor : 0,
      isPositiveOrZero: true,
    });
    this.shareCapitalAgainst = new NumberFieldModel({
      value: data.type === "voted" ? data.shareCapitalAgainst : 0,
      isPositiveOrZero: true,
    });
    this.shareCapitalBlank = new NumberFieldModel({
      value: data.type === "voted" ? data.shareCapitalBlank : 0,
      isPositiveOrZero: true,
    });
    if (data.type === "live" || data.type === "individualVotes") {
      this.setVotingData(data);
    }
    this.selectedVotingMode = votingOptionsMappings[this.type];
    this.votingInput = votingInput;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setNewSelectedVotingType(votingType: GeneralMeetingVotingType) {
    this.newSelectedVotingType = votingType;
  }

  saveVotingType() {
    this.setVotingType(this.newSelectedVotingType);
    this.closeVotingTypeSettingsDialog();
  }

  openVotingTypeSettingsDialog(defaultVotingType: GeneralMeetingVotingType) {
    this.newSelectedVotingType = this.selectedVotingType(defaultVotingType);
  }

  closeVotingTypeSettingsDialog() {
    this.newSelectedVotingType = undefined;
  }

  selectedVotingType(defaultVotingType: GeneralMeetingVotingType) {
    return this.votingType ?? defaultVotingType;
  }

  get votingWeight(): number {
    return this.votingInput?.shareholderData.shareTypes[0]?.vote ?? 1;
  }

  get isSameVotingWeight(): boolean | undefined {
    const shareTypes = this.votingInput?.shareholderData.shareTypes;
    return shareTypes?.every((shareType) => shareType.vote === shareTypes[0]?.vote);
  }

  get shareNominalValue() {
    return this.votingInput?.shareNominalValue ?? 1;
  }

  calculateShareCapital(votes: number | undefined): number {
    return Big(votes ?? 0)
      .div(this.votingWeight)
      .mul(this.shareNominalValue)
      .toNumber();
  }

  setLiveVotingData(
    votesFor: number,
    votesAgainst: number,
    representedVotes: number,
    shareCapitalFor: number,
    shareCapitalAgainst: number,
    representedShareCapital: number,
  ) {
    this.liveVoting = {
      votesFor,
      votesAgainst,
      shareCapitalFor,
      shareCapitalAgainst,
    };
    this.updateRepresentedVotes(representedVotes);
    this.updateRepresentedShareCapital(representedShareCapital);
  }

  updateRepresentedVotes(value: number) {
    this.representedVotes = value;
  }

  updateRepresentedShareCapital(value: number) {
    this.representedShareCapital = value;
  }

  toJSON(): GeneralMeetingVoting {
    switch (this.type) {
      case "live":
        return liveVotes(
          this.liveVoting!.votesFor,
          this.liveVoting!.votesAgainst,
          this.representedVotes,
          this.liveVoting!.shareCapitalFor,
          this.liveVoting!.shareCapitalAgainst,
          this.representedShareCapital,
        );
      case "individualVotes":
        return individualVotes(
          this.individualVoting?.votesFor,
          this.individualVoting?.votesAgainst,
          this.individualVoting?.votesBlank,
          this.individualVoting?.shareCapitalFor,
          this.individualVoting?.shareCapitalAgainst,
          this.individualVoting?.shareCapitalBlank,
        );
      case "unanimous":
      case "notVoted":
        return { type: this.type };
      case "voted":
        return voted(
          this.votesFor.toNumberOrZero(),
          this.votesAgainst.toNumberOrZero(),
          this.votesBlank.toNumberOrZero(),
          this.shareCapitalFor.toNumberOrZero(),
          this.shareCapitalAgainst.toNumberOrZero(),
          this.shareCapitalBlank.toNumberOrZero(),
        );
    }
  }

  setVotingData(voting: GeneralMeetingVoting) {
    if (voting.type === "voted") {
      this.votesFor.setAndCommitInputValue(voting.votesFor);
      this.votesAgainst.setAndCommitInputValue(voting.votesAgainst);
      this.votesBlank.setAndCommitInputValue(voting.votesBlank);
      this.shareCapitalFor.setAndCommitInputValue(voting.shareCapitalFor);
      this.shareCapitalAgainst.setAndCommitInputValue(voting.shareCapitalAgainst);
      this.shareCapitalBlank.setAndCommitInputValue(voting.shareCapitalBlank);
    } else if (voting.type === "live") {
      this.setLiveVotingData(
        voting.votesFor,
        voting.votesAgainst,
        voting.representedVotes,
        voting.shareCapitalFor,
        voting.shareCapitalAgainst,
        voting.representedShareCapital,
      );
    } else if (voting.type === "individualVotes") {
      this.individualVoting = {
        votesFor: voting.votesFor,
        votesAgainst: voting.votesAgainst,
        votesBlank: voting.votesBlank,
        shareCapitalFor: voting.shareCapitalFor,
        shareCapitalAgainst: voting.shareCapitalAgainst,
        shareCapitalBlank: voting.shareCapitalBlank,
      };
      this.updateRepresentedVotes(voting.votesFor + voting.votesAgainst + voting.votesBlank);
      this.updateRepresentedShareCapital(
        voting.shareCapitalFor + voting.shareCapitalAgainst + voting.shareCapitalBlank,
      );
    }
  }

  setVotingType(votingType?: GeneralMeetingVotingType) {
    this.votingType = votingType;
  }

  update(voting: GeneralMeetingVoting) {
    this.type = voting.type;
    this.setVotingData(voting);
    switch (this.type) {
      case "live":
        this.selectedVotingMode = VotingModeOption.LIVE_VOTING;
        break;
      case "unanimous":
        this.selectedVotingMode = VotingModeOption.UNANIMOUS;
        break;
      case "voted":
        this.selectedVotingMode = VotingModeOption.MANUAL;
        break;
      case "individualVotes":
        this.selectedVotingMode = VotingModeOption.INDIVIDUAL;
        break;
    }
  }

  setSelectedVotingMode(mode: VotingModeOption, data?: LiveVotingResults) {
    this.selectedVotingMode = mode;
    switch (mode) {
      case VotingModeOption.LIVE_VOTING:
        this.type = "live";
        if (data) {
          this.setLiveVotingData(
            data.votesFor.numberOfVotes,
            data.votesAgainst.numberOfVotes,
            data.representedVotes,
            data.votesFor.sharecapital,
            data.votesAgainst.sharecapital,
            data.representedShareCapital,
          );
        }
        break;
      case VotingModeOption.UNANIMOUS:
        this.type = "unanimous";
        break;
      case VotingModeOption.MANUAL:
        this.type = "voted";
        break;
      case VotingModeOption.INDIVIDUAL:
        this.type = "individualVotes";
        break;
    }
  }

  get isVotesRegistered() {
    switch (this.type) {
      case "notVoted":
        return false;
      case "unanimous":
        return true;
      case "individualVotes":
        return this.isIndividualVotesRegistered;
      case "live":
        return this.liveVoting !== undefined;
      case "voted":
        return this.isManualVotesRegistered;
    }
  }

  get isManualVotesRegistered() {
    return (
      this.votesFor.toNumberOrZero() > 0 ||
      this.votesAgainst.toNumberOrZero() > 0 ||
      this.votesBlank.toNumberOrZero() > 0
    );
  }

  get isIndividualVotesRegistered() {
    return (
      this.individualVoting !== undefined &&
      (this.individualVoting.votesFor > 0 ||
        this.individualVoting.votesAgainst > 0 ||
        this.individualVoting.votesBlank > 0)
    );
  }

  get numberOfLiveVotesCast() {
    return this.liveVoting ? this.liveVoting.votesFor + this.liveVoting.votesAgainst : 0;
  }

  get numberOfIndividualVotesCast() {
    return this.individualVoting
      ? this.individualVoting.votesFor + this.individualVoting.votesAgainst
      : 0;
  }
}

export class GeneralMeetingVotesModel {
  meetChair: GeneralMeetingVotingModel;
  approveNoticeAndAgenda: GeneralMeetingVotingModel;
  decisions: Map<string, GeneralMeetingVotingModel>;
  votingInput?: VotingInputData;

  constructor(data: GeneralMeetingVotes, votingInput?: VotingInputData) {
    this.meetChair = new GeneralMeetingVotingModel(data.meetChair, votingInput);
    this.approveNoticeAndAgenda = new GeneralMeetingVotingModel(
      data.approveNoticeAndAgenda,
      votingInput,
    );
    this.decisions = new Map(
      data.decisions.map((d) => [
        d.decisionId,
        new GeneralMeetingVotingModel(d.voting, votingInput, d.votingType),
      ]),
    );
    this.votingInput = votingInput;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  toJSON(): GeneralMeetingVotes {
    return {
      meetChair: this.meetChair.toJSON(),
      approveNoticeAndAgenda: this.approveNoticeAndAgenda.toJSON(),
      decisions: Array.from(this.decisions.entries()).map(([decisionId, voting]) => ({
        decisionId,
        voting: voting.toJSON(),
        votingType: voting.votingType,
      })),
    };
  }

  getVotingForDecision(decisionId: string): GeneralMeetingVotingModel {
    return (
      this.decisions.get(decisionId) ??
      this.decisions
        .set(decisionId, new GeneralMeetingVotingModel(unanimous(), this.votingInput))
        .get(decisionId)!
    );
  }
}

type NewSignerFormParams = {
  signer?: keyof SelectedSigners;
  additionalCosignerIndex?: number;
  inputValue: string;
  onSubmit: (data: RegisterNewSignerEvent) => void;
  onCancel: () => void;
};

export class NewSignerForm {
  params: NewSignerFormParams;
  name: StringFieldModel;
  email: StringFieldModel;
  isSaving?: boolean;

  constructor(params: NewSignerFormParams) {
    this.params = params;
    this.name = new StringFieldModel({
      value: params.inputValue,
      required: true,
      validator: validateName,
    });
    this.email = createEmailField({ required: true });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validate() {
    this.name.validate();
    this.email.validate();
    return this.name.isValid && this.email.isValid;
  }

  submit() {
    if (this.validate()) {
      this.isSaving = true;
      this.params.onSubmit(
        registerNewSignerEvent(this.name.validatedValue, this.email.validatedValue),
      );
    }
  }

  cancel() {
    if (!this.isSaving) {
      this.params.onCancel();
    }
  }
}

function numberOfSharesForShareholders(shareholders: ExtendedShareholderDTO[]) {
  return shareholders.reduce<Record<string, number>>((sum, shareholder) => {
    Object.entries(shareholder.calculatedShares).forEach(([key, shares]) => {
      sum[key] = (sum[key] ?? 0) + shares;
    });
    return sum;
  }, {});
}

export function handleRegisterNewSignerResult(
  event: RegisterNewSignerResultEvent,
  newSigner: NewSignerForm | null,
  context: ProjectContext,
  dispatch: DbotEventDispatcher,
) {
  if (newSigner) {
    if (isOk(event.result)) {
      if (newSigner.params.signer === "meetingChair") {
        context.generalMeetingFields.meetingChair.setValue(
          mapRoleToCompanyRoleIdWithName(event.result.data),
        );
      } else if (newSigner.params.signer === "coSigner") {
        context.generalMeetingFields.coSigner.setValue(
          mapRoleToCompanyRoleIdWithName(event.result.data),
        );
      } else if (newSigner.params.additionalCosignerIndex !== undefined) {
        context.generalMeetingFields.additionalCoSigners.replace(
          newSigner.params.additionalCosignerIndex,
          {
            id: event.result.data.companyroleId.toString(),
            name: getFullName(event.result.data),
            isSelected: true,
          },
        );
      }
      context.generalMeetingFields.meetingChair.options.push(
        mapRoleToCompanyRoleIdWithName(event.result.data),
      );
      context.generalMeetingFields.coSigner.options.push(
        mapRoleToCompanyRoleIdWithName(event.result.data),
      );
    } else {
      dispatch({
        type: "show-alert",
        message: { key: "decisions.newSigner.saveError" },
        messageType: "error",
      });
    }
  }
}

export class GeneralMeetingMinutesForm implements WithInitialization, WithAttachmentSupport {
  readonly type = DocumentType.general_meeting;
  readonly dispatch: DbotEventDispatcher;
  context: DocumentContext<GeneralMeetingMinutes>;

  companyHasMultipleStockHolders: boolean;
  votes: GeneralMeetingVotesModel;
  companySoleStockholder: StringFieldModel;

  // new fields in the model
  isAttendingOpen = false;
  isUpdatingParticipationData = false;
  isVotingOpen = false;

  newSigner: NewSignerForm | null = null;

  documentLanguage: RadioFieldModel<DocumentLanguage>;

  existingParticipation: Map<number, GeneralMeetingParticipation> = new Map<
    number,
    GeneralMeetingParticipation
  >();
  newParticipation: Map<number, GeneralMeetingParticipation> = new Map<
    number,
    GeneralMeetingParticipation
  >();
  poaMap: Map<number, PowerOfAttorneyForGeneralMeeting> = new Map<
    number,
    PowerOfAttorneyForGeneralMeeting
  >();

  representShareholders: Map<number, RepresentativeOption> = new Map<
    number,
    RepresentativeOption
  >();
  shareholderRepresentation: ShareholderParticipation[];

  repairTextDeadlineGeneralMeeting: boolean;
  isTooShortNotice = false;
  powersOfAttorney: PowerOfAttorneyForGeneralMeeting[] = [];
  decisionTextModels: DecisionTextModel[];

  currentVotingType?: GeneralMeetingVoteType;
  currentVotingDecisionId: string | null = null;
  saveVotesState: "saving" | "error" | null = null;
  individualVotesMeetChair: GeneralMeetingIndividualVote[] = [];
  individualVotesApproveNoticeAndAgenda: GeneralMeetingIndividualVote[] = [];
  individualVotesDecisions: { [key: string]: GeneralMeetingIndividualVote[] } = {};

  attachments: Map<string, UploadedAttachment> = new Map<string, UploadedAttachment>();

  additionalDetailsTextModel: LocalizedTextEditorModel;
  additionalDetailsTexts: LocalizedText[]; // initial value
  validateMessageError: TMessage[] = [];

  constructor(dispatch: DbotEventDispatcher, context: DocumentContext<GeneralMeetingMinutes>) {
    makeAutoObservable(this, { type: false, dispatch: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    const data = context.documentData;

    this.poaMap = new Map(context.powerOfAttorneys?.map((p) => [p.shareholderId, p]));
    ({
      existingParticipation: this.existingParticipation,
      newParticipation: this.newParticipation,
      representShareholders: this.representShareholders,
      shareholderRepresentation: this.shareholderRepresentation,
    } = mapToParticipationData({
      orgNo: this.organizationNumber,
      participationList: context.participationList,
      poaMap: this.poaMap,
      shareholderData: this.context.shareholderData,
      roles: context.companyInfo.roles,
      defaultPresentValue: !context.generalMeetingFields.conductElectronicGeneralMeeting.value,
      shareholderParticipation: data.shareholderParticipation,
    }));

    this.companyHasMultipleStockHolders =
      data.companyHasMultipleStockHolders ?? this.numberOfShareholders > 1;

    this.votes = createGeneralMeetingVotingModel(context, data);
    this.repairTextDeadlineGeneralMeeting = data.repairTextDeadlineGeneralMeeting;
    this.companySoleStockholder = new StringFieldModel({ value: data.companySoleStockholder });

    this.documentLanguage = requiredOfficialDocumentLanguageFieldModel({
      orgNo: this.organizationNumber,
      value: mapLocalesToDocumentLanguage(data.documentLanguage),
    });

    this.attachments = new Map(data.attachments?.map((a) => [a.id, a]));

    this.additionalDetailsTexts = data?.additionalDetailsTexts ?? [];

    this.additionalDetailsTextModel = new LocalizedTextEditorModel({
      texts: data?.additionalDetailsTexts,
      isReadOnly: () => context.isLocked,
      label: { key: "documents.minutes.additionalDetails" },
      aiClient: context.aiClient,
    });
    this.decisionTextModels = mapLocalizedDecisionTextsToModel(
      context,
      dispatch,
      DocumentType.general_meeting,
      this.documentLanguage.value,
    );
  }

  get decisionMatters() {
    return filterDecisionRequiringVoting(
      this.context.currentDecisions,
      DocumentType.general_meeting,
    );
  }

  init(): CleanupFunction {
    this.dispatch({ type: "dbot/fetch-general-meeting-votes" });

    const cleanupFunctions = [autorun(() => this.handleLanguageChanges())];

    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }

  get currentDecisions(): DecisionItem[] {
    return this.context.currentDecisions.filter((d) => d.includeInGeneralMeeting);
  }

  get currentDocuments(): DocumentItem[] {
    return this.context.currentDocuments;
  }

  private updateMinutesLiveParticipation(event: UpdateMinutesLiveParticipationEvent) {
    ({
      existingParticipation: this.existingParticipation,
      newParticipation: this.newParticipation,
      representShareholders: this.representShareholders,
      shareholderRepresentation: this.shareholderRepresentation,
    } = mapToParticipationData({
      orgNo: this.organizationNumber,
      participationList: event.participationList,
      poaMap: this.poaMap,
      shareholderData: this.context.shareholderData,
      roles: this.context.companyInfo.roles,
      defaultPresentValue: !this.context.generalMeetingFields.conductElectronicGeneralMeeting.value,
      shareholderParticipation: this.shareholderRepresentation,
    }));
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/decision-added":
        this.initDecisionTextModel(event.decisionId);
        if (!this.votes.decisions.has(event.decisionId)) {
          const voting = event.isFreetext ? notVoted() : unanimous();
          this.votes.decisions.set(
            event.decisionId,
            new GeneralMeetingVotingModel(voting, this.votes.votingInput),
          );
        }
        break;
      case "dbot/decision-removed":
        this.votes.decisions.delete(event.decisionId);
        break;
      case "dbot/general-meeting-short-notice-status-changed":
        this.repairTextDeadlineGeneralMeeting = event.repairTextDeadlineGeneralMeeting;
        this.isTooShortNotice = event.isTooShortNotice;
        break;
      case "dbot/register-new-signer-result":
        handleRegisterNewSignerResult(event, this.newSigner, this.context, this.dispatch);
        this.newSigner = null;
        break;
      case "dbot/fetch-general-meeting-votes-result":
        this.individualVotesMeetChair = event.votes.meetChair;
        this.individualVotesApproveNoticeAndAgenda = event.votes.approveNoticeAndAgenda;
        this.individualVotesDecisions = event.votes.decisions;
        this.updateVotes();
        break;
      case "dbot/save-general-meeting-votes-result":
        if (event.result === "ok") {
          this.updateNumericVotingValues();
          this.hideVotingDialog();
          this.setSaveVotesState(null);
        } else {
          this.setSaveVotesState("error");
        }
        break;
      case "dbot/upload-attachments-result":
        if (event.source === this && isOk(event.result)) {
          event.result.data.forEach((attachment) => {
            this.attachments.set(attachment.id, attachment);
          });
        }
        break;
      case "dbot/delete-attachment-result":
        if (event.source === this) {
          this.removeAttachment(event.attachmentId);
        }
        break;
      case "dbot/update-voting-in-general-meeting":
        this.updateVotingInGeneralMeeting(event.voting, event.voteType, event.decisionId);
        break;
      case "dbot/update-all-votes-in-general-meeting":
        this.updateVotingInGeneralMeeting(event.votes.meetChair, GeneralMeetingVoteType.meetChair);
        this.updateVotingInGeneralMeeting(
          event.votes.approveNoticeAndAgenda,
          GeneralMeetingVoteType.approveNoticeAndAgenda,
        );
        event.votes.decisions.map((decision) =>
          this.updateVotingInGeneralMeeting(
            decision.voting,
            GeneralMeetingVoteType.decision,
            decision.decisionId,
          ),
        );
        break;
      case "dbot/update-voting-type-in-general-meeting":
        this.updateVotingTypeInGeneralMeeting(event.voteType, event.decisionId, event.votingType);
        break;
      case "dbot/update-minutes-live-participation":
        this.updateMinutesLiveParticipation(event);
        break;
    }
  }

  isDecisionMatter(decisionId: string): boolean {
    return this.decisionMatters.includes(decisionId);
  }

  calculatePresenceForSharetype(type: ShareType): number {
    const presentShares = this.numberOfPresentShares[type.sharetypeCode];
    const totalWithoutCompany =
      this.projectShareholderData.shareSumWithoutCompany[type.sharetypeCode];
    return Math.floor(((100 * presentShares) / totalWithoutCompany) * 100) / 100 || 0;
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.context.fileUploadConfig;
  }

  get shareNominalValue(): number {
    return (
      this.context.inputStates[0]?.sharenominalValue ?? this.context.companyInfo.shareNominalValue
    );
  }

  get representedVotes() {
    return this.shareholderRepresentation.reduce((a, b) => a + b.votesParticipating, 0);
  }

  get representedShareCapital() {
    return this.shareholderRepresentation.reduce(
      (a, b) => a + b.sharesParticipating * this.shareNominalValue,
      0,
    );
  }

  updateGeneralMeetingVotes(
    meetChair: GeneralMeetingIndividualVote[],
    approveNoticeAndAgenda: GeneralMeetingIndividualVote[],
    decisions: {
      [key: string]: GeneralMeetingIndividualVote[];
    },
    type: "live" | "individualVotes" = "individualVotes",
  ) {
    this.votes.meetChair.setVotingData(this.calculateAllVotes(meetChair, type));
    this.votes.approveNoticeAndAgenda.setVotingData(
      this.calculateAllVotes(approveNoticeAndAgenda, type),
    );
    this.votes.decisions.forEach((vote, decisionId) => {
      const decisionVotes = decisions[decisionId];
      if (decisionVotes !== undefined) {
        vote.setVotingData(this.calculateAllVotes(decisionVotes, type));
      }
    });
  }

  calculateAllVotes(
    votes: GeneralMeetingIndividualVote[],
    type: "live" | "individualVotes" = "individualVotes",
  ) {
    return calculateAllVotes(
      votes,
      this.presentShareholdersById,
      this.projectShareholderData.shareTypes,
      this.shareNominalValue,
      type,
      this.representedVotes,
      this.representedShareCapital,
    );
  }

  private updateNumericVotingValues() {
    switch (this.currentVotingType) {
      case "meetChair":
        this.updateVotingInGeneralMeeting(
          this.calculateAllVotes(this.individualVotesMeetChair),
          this.currentVotingType,
        );
        break;
      case "approveNoticeAndAgenda":
        this.updateVotingInGeneralMeeting(
          this.calculateAllVotes(this.individualVotesApproveNoticeAndAgenda),
          this.currentVotingType,
        );
        break;
      case "decision":
        this.updateVotingInGeneralMeeting(
          this.calculateAllVotes(this.individualVotesDecisions[this.currentVotingDecisionId!]),
          this.currentVotingType,
          this.currentVotingDecisionId!,
        );
        break;
    }
  }

  private updateVotingTypeInGeneralMeeting(
    voteType: GeneralMeetingVoteType,
    decisionId?: string,
    votingType?: GeneralMeetingVotingType,
  ) {
    switch (voteType) {
      case "meetChair":
      case "approveNoticeAndAgenda":
        break;
      case "decision":
        this.votes.decisions.get(decisionId!)!.setVotingType(votingType);
        break;
    }
  }

  private updateVotingInGeneralMeeting(
    voting: GeneralMeetingVoting,
    voteType: GeneralMeetingVoteType,
    decisionId?: string,
  ) {
    switch (voteType) {
      case "meetChair":
        this.votes.meetChair.update(voting);
        break;
      case "approveNoticeAndAgenda":
        this.votes.approveNoticeAndAgenda.update(voting);
        break;
      case "decision":
        this.votes.decisions.get(decisionId!)!.update(voting);
        break;
    }
  }

  get shareholdersEligibleForVoting() {
    return activeShareholdersEligibleForVoting(
      this.projectShareholderData.shareholders,
      this.projectShareholderData.shareTypes,
    );
  }

  get displayIneligibleShareholdersForVotingWarning(): boolean {
    return (
      this.projectShareholderData.shareholders.length !== this.shareholdersEligibleForVoting.length
    );
  }

  initVotes(): GeneralMeetingIndividualVote[] {
    return this.shareholdersEligibleForVoting
      .filter((s) => s.birthDateOrOrganisationNumber !== this.organizationNumber)
      .filter(
        (s) =>
          this.existingParticipation.get(s.id)?.isPresent ||
          this.newParticipation.get(s.id)?.isPresent,
      )
      .map((shareholder) => ({
        id: newUuid(),
        shareholderId: shareholder.id,
        vote: "for",
      }));
  }

  get activeShareholders() {
    return this.projectShareholderData.shareholders
      .filter(hasShares)
      .filter((s) => s.birthDateOrOrganisationNumber !== this.organizationNumber);
  }

  get isAllShareholdersPresent(): boolean {
    return this.activeShareholders.every((shareholder) =>
      this.isShareholderPresent(shareholder.id),
    );
  }

  toggleAllShareholdersPresent(value: boolean) {
    this.activeShareholders.map((shareholder) => {
      const participation = this.getParticipation(shareholder.id);
      if (participation) {
        participation.isPresent = value;
      } else {
        this.newParticipation.set(shareholder.id, {
          participationId: newUuid(),
          shareholderId: shareholder.id,
          isPresent: value,
        });
      }
    });
  }

  toggleShareholderPresent(shareholderId: number) {
    const participation = this.getParticipation(shareholderId);
    if (participation) {
      participation.isPresent = !participation.isPresent;
    } else {
      this.newParticipation.set(shareholderId, {
        participationId: newUuid(),
        shareholderId: shareholderId,
        isPresent: true,
      });
    }
  }

  private getParticipation(shareholderId: number) {
    return (
      this.existingParticipation.get(shareholderId) ?? this.newParticipation.get(shareholderId)
    );
  }

  updateShareholderRepresentation() {
    this.shareholderRepresentation = updateShareholderParticipation(
      this.numberOfPresentShares,
      this.projectShareholderData.shareTypes,
      this.projectShareholderData.shareSumWithoutCompany,
    );
    this.isUpdatingParticipationData = false;
  }

  saveParticipation() {
    this.isUpdatingParticipationData = true;
    this.updateShareholderRepresentation();
    this.dispatch({
      type: "dbot/save-participation",
      createParticipation: Array.from(this.newParticipation.values()),
      updateParticipation: Array.from(this.existingParticipation.values()),
    });

    this.updateVotes();
  }

  updateVotes() {
    this.updateGeneralMeetingVotes(
      this.individualVotesMeetChair,
      this.individualVotesApproveNoticeAndAgenda,
      this.individualVotesDecisions,
      this.isElectronicGeneralMeeting ? "live" : "individualVotes",
    );
  }

  setMeetChairVoting(voting: GeneralMeetingVoting) {
    this.votes.meetChair.update(voting);
    this.individualVotesMeetChair = [];
  }

  setApproveNoticeAndAgendaVoting(voting: GeneralMeetingVoting) {
    this.votes.approveNoticeAndAgenda.update(voting);
    this.individualVotesApproveNoticeAndAgenda = [];
  }

  setVotingForDecision(decisionId: string, voting: GeneralMeetingVoting) {
    if (!this.votes.decisions.has(decisionId)) {
      this.votes.decisions.set(
        decisionId,
        new GeneralMeetingVotingModel(voting, this.votes.votingInput),
      );
    } else {
      this.votes.decisions.get(decisionId)!.update(voting);
    }
  }

  showAttendingDialog() {
    this.isAttendingOpen = true;
  }

  hideAttendingDialog() {
    this.isAttendingOpen = false;
  }

  closeNewSigner() {
    this.newSigner = null;
  }

  setRepresentShareholderValue(shareholderId: number, value: RepresentativeOption | null) {
    const participation = this.getParticipation(shareholderId);
    if (value) {
      this.representShareholders.set(shareholderId, value);
      if (participation) {
        participation.representedByName = value.name;
        participation.representedById = value.companyRoleId;
      } else {
        this.newParticipation.set(shareholderId, {
          representedByName: value.name,
          representedById: value.companyRoleId,
          shareholderId: shareholderId,
          participationId: newUuid(),
          isPresent: false,
        });
      }
    } else {
      this.representShareholders.delete(shareholderId);
      if (participation) {
        participation.representedByName = undefined;
        participation.representedById = undefined;
      }
    }
  }

  get organizationNumber() {
    return this.context.companyInfo?.company.organizationnumber;
  }

  get numberOfShareholders() {
    return this.projectShareholderData.shareholders?.length ?? 0;
  }

  get projectShareholderData() {
    return {
      shareholders: this.context.shareholderData?.shareholders ?? [],
      shareTypes: this.context.shareholderData?.shareTypes ?? [],
      shareSumByType: this.context.shareholderData?.shareSumByType ?? {},
      shareSumWithoutCompany: this.context.shareholderData?.shareSumWithoutCompany ?? {},
    };
  }

  get shareholderNamesById() {
    return Object.fromEntries(
      this.context.shareholderData?.shareholders.map((shareholder) => [
        shareholder.id,
        shareholder.name,
      ]),
    );
  }

  get presentShareholdersById() {
    const shareholders = this.projectShareholderData.shareholders.filter(
      (s) =>
        s.birthDateOrOrganisationNumber !== this.organizationNumber &&
        this.getParticipation(s.id)?.isPresent,
    );
    return Object.fromEntries(shareholders.map((shareholder) => [shareholder.id, shareholder]));
  }

  get meetingForm(): RadioFieldModel<GeneralMeetingForm> {
    return this.context.generalMeetingFields.meetingForm;
  }

  get generalMeetingType(): RadioFieldModel<GeneralMeetingType> {
    return this.context.generalMeetingFields.generalMeetingType;
  }

  get numberOfPresentShares(): Record<string, number> {
    const shareholders = this.projectShareholderData.shareholders.filter(
      (s) =>
        s.birthDateOrOrganisationNumber !== this.organizationNumber &&
        this.getParticipation(s.id)?.isPresent,
    );

    return numberOfSharesForShareholders(shareholders);
  }

  get numberOfPresentShareholders(): number {
    return this.projectShareholderData.shareholders.filter(
      (s) =>
        s.birthDateOrOrganisationNumber !== this.organizationNumber &&
        this.getParticipation(s.id)?.isPresent,
    ).length;
  }

  get isLocked(): boolean {
    return this.context.isLocked;
  }

  setSimplifiedGeneralMeeting(value: boolean) {
    this.context.generalMeetingFields.isSimplifiedGeneralMeeting.setValue(value);
    if (this.context.generalMeetingFields.isSimplifiedGeneralMeeting.value) {
      this.context.generalMeetingFields.coSigner.setValue(undefined);
    }
  }

  representShareholdersOptions(shareholderId: number): RepresentativeOption[] {
    const options: RepresentativeOption[] = [];
    const chairperson = getChairperson(this.context.companyInfo.roles);
    if (chairperson)
      options.push({
        name: chairperson.name,
        companyRoleId: chairperson.companyroleId,
      });
    const ceo = getCEO(this.context.companyInfo.roles);
    if (ceo)
      options.push({
        name: ceo.name,
        companyRoleId: ceo.companyroleId,
      });
    const poa = this.getPowerOfAttorneyForShareholder(shareholderId);
    if (poa && poa?.poaRecipientRole === "other") {
      options.push({
        name: poa.poaRecipientName,
      });
    }
    return options;
  }

  filterRepresentShareholdersOptions(
    params: {
      items: RepresentativeOption[];
      labelFn: (item: RepresentativeOption) => string;
    },
    inputValue: string,
  ) {
    const filtered = defaultFilterFn(params, inputValue);
    if (inputValue.trim() && params.items.every((d) => d.name !== inputValue)) {
      return [...filtered, { name: inputValue }];
    }
    return filtered;
  }

  getPowerOfAttorneyForShareholder(id: number) {
    return this.powersOfAttorney.find((poa) => poa.shareholderId === id);
  }

  isShareholderPresent(id: number) {
    const participation = this.getParticipation(id);
    return participation?.isPresent ?? false;
  }

  hideVotingDialog() {
    this.isVotingOpen = false;
    this.currentVotingType = undefined;
    this.currentVotingDecisionId = null;
  }

  get isElectronicGeneralMeeting() {
    return this.context.generalMeetingFields.conductElectronicGeneralMeeting.value ?? false;
  }

  showMeetingChairVoting() {
    if (!this.isElectronicGeneralMeeting) {
      if (this.individualVotesMeetChair.length === 0) {
        this.individualVotesMeetChair = this.initVotes();
      }
      this.isVotingOpen = true;
      this.currentVotingType = GeneralMeetingVoteType.meetChair;
    }
  }

  showApproveNoticeAndAgendaVoting() {
    if (!this.isElectronicGeneralMeeting) {
      if (this.individualVotesApproveNoticeAndAgenda.length === 0) {
        this.individualVotesApproveNoticeAndAgenda = this.initVotes();
      }
      this.isVotingOpen = true;
      this.currentVotingType = GeneralMeetingVoteType.approveNoticeAndAgenda;
    }
  }

  showDecisionVotingDialog(decisionId: string) {
    if (!this.isElectronicGeneralMeeting) {
      this.currentVotingType = GeneralMeetingVoteType.decision;
      this.currentVotingDecisionId = decisionId;
      if (
        !this.individualVotesDecisions[decisionId] ||
        this.individualVotesDecisions[decisionId].length === 0
      ) {
        this.individualVotesDecisions[decisionId] = this.initVotes();
      }
      this.isVotingOpen = true;
    }
  }

  saveVotes() {
    if (this.isElectronicGeneralMeeting) {
      this.hideVotingDialog();
    } else {
      this.setSaveVotesState("saving");
      this.dispatch({
        type: "dbot/save-general-meeting-votes",
        voteType: this.currentVotingType!,
        decisionId: this.currentVotingDecisionId,
        votes: this.currentVotes,
      });
    }
  }

  get currentVotes(): GeneralMeetingIndividualVote[] {
    switch (this.currentVotingType) {
      case "meetChair":
        return this.individualVotesMeetChair.filter(
          (v) => this.presentShareholdersById[v.shareholderId] !== undefined,
        );
      case "approveNoticeAndAgenda":
        return this.individualVotesApproveNoticeAndAgenda.filter(
          (v) => this.presentShareholdersById[v.shareholderId] !== undefined,
        );
      case "decision":
        return this.individualVotesDecisions[this.currentVotingDecisionId!]!.filter(
          (v) => this.presentShareholdersById[v.shareholderId] !== undefined,
        );
    }
    return [];
  }

  setIndividualVote(shareholderId: number, vote: IndividualVote) {
    const v = this.currentVotes.find((vote) => vote.shareholderId === shareholderId);
    if (v) v.vote = vote;
  }

  get isSavingGeneralMeetingVotes() {
    return this.saveVotesState === "saving";
  }

  get isSaveGeneralMeetingError() {
    return this.saveVotesState === "error";
  }

  setSaveVotesState(state: "saving" | "error" | null) {
    this.saveVotesState = state;
  }

  removeAttachment(attachmentId: string) {
    this.attachments.delete(attachmentId);
  }

  uploadAttachments(files: File[]) {
    if (!files.length) {
      return;
    }
    const document = this.context.currentDocuments.find(
      (d) => d.type === DocumentType.general_meeting,
    );
    this.dispatch(
      uploadAttachmentsEvent({
        source: this,
        files,
        reference: document!.documentId,
        attachmentType: AttachmentType.UPLOADED_FILE,
      }),
    );
  }

  deleteAttachmentById(attachmentId: string) {
    this.dispatch(deleteAttachmentEvent(attachmentId, this));
  }

  openAttachment(attachmentId: string) {
    this.dispatch({ type: "dbot/open-attachment", attachmentId });
  }

  getUploadedAttachment() {
    return Array.from(this.attachments.entries());
  }

  get allShareholdersPresent() {
    return this.numberOfPresentShareholders === this.numberOfShareholders;
  }

  get locale() {
    return this.context.locale;
  }

  get documentId() {
    return this.context.documentId;
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get openSendForReviewDialogAction() {
    return openSendForReviewDialogAction(
      { document: this.documentItem, signingStatus: this.documentStatus?.signingStatus },
      this.context,
    );
  }

  get decisionAttachments() {
    return this.currentDecisions
      .flatMap((a) => a.attachments)
      .filter((a) => a.selectedDocuments.includes(DocumentType.general_meeting));
  }

  get includesUnsupportedDocumentAttachments() {
    return (
      this.attachments.size > 0 &&
      Array.from(this.attachments.values()).some((a) => !isExtensionPdf(a.filename))
    );
  }

  get includesUnsupportedDecisionAttachments() {
    return (
      this.decisionAttachments.length > 0 &&
      this.decisionAttachments.some((a) => !isExtensionPdf(a.filename))
    );
  }

  get openSendForSigningDialogAction() {
    return openSendForSigningDialogAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      showUnsupportedAttachmentWarning:
        this.includesUnsupportedDocumentAttachments || this.includesUnsupportedDecisionAttachments,
    });
  }

  get isValid(): boolean {
    const { isSimplifiedGeneralMeeting, coSigner } = this.context.generalMeetingFields;
    return isSimplifiedGeneralMeeting.isValid && coSigner.isValid;
  }

  validate() {
    this.validateMessageError = [];
    const { isSimplifiedGeneralMeeting, coSigner } = this.context.generalMeetingFields;
    isSimplifiedGeneralMeeting.validate();
    coSigner.validate();
    if (!coSigner.isValid) {
      this.validateMessageError.push({
        key: "documents.generalMeetingMinutes.sendForSigningError",
      });
    }
    return this.isValid;
  }

  get downloadSignedDocumentAction() {
    return downloadSignedDocumentAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      numberOfSignedDocuments: this.documentStatus?.numberOfSignedDocuments,
    });
  }

  get hasNotice() {
    return this.currentDocuments.some(
      (document) => document.type === DocumentType.notice_general_meeting,
    );
  }

  initDecisionTextModel(decisionId: string) {
    const decisionTextModel = this.decisionTextModels.find((d) => d.decisionId === decisionId);
    if (decisionTextModel === undefined) {
      const decision = this.context.currentDecisions.find((d) => d.decisionId === decisionId);
      const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
      if (decision) {
        const model = mapDecisionTextModel(
          this.dispatch,
          decision,
          DocumentType.general_meeting,
          this.context,
          documentLanguages,
        );
        this.decisionTextModels.push(model);
      }
    }
  }

  private handleLanguageChanges() {
    const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
    if (isLanguageChanged(documentLanguages, this.additionalDetailsTextModel.textModels)) {
      runInAction(() => {
        this.additionalDetailsTextModel.onLanguageChange(
          documentLanguages,
          this.additionalDetailsTexts,
        );
        this.decisionTextModels = mapLocalizedDecisionTextsToModel(
          this.context,
          this.dispatch,
          DocumentType.general_meeting,
          this.documentLanguage.value,
        );
      });
    }
  }

  addCoSigner() {
    this.additionalCoSigners.add({
      id: newUuid(),
      name: "",
      isSelected: false,
    });
  }

  get additionalCoSigners() {
    return this.context.generalMeetingFields.additionalCoSigners;
  }

  openNewSigner(
    option: NewSigner,
    signer?: keyof SelectedSigners,
    additionalCosignerIndex?: number,
  ) {
    if (signer) {
      this.newSigner = new NewSignerForm({
        signer: signer,
        inputValue: option.inputValue,
        onSubmit: this.dispatch,
        onCancel: this.closeNewSigner,
      });
    } else if (additionalCosignerIndex !== undefined) {
      this.newSigner = new NewSignerForm({
        additionalCosignerIndex: additionalCosignerIndex,
        inputValue: option.inputValue,
        onSubmit: this.dispatch,
        onCancel: this.closeNewSigner,
      });
    }
  }
}

export function applyForm(form: GeneralMeetingMinutesForm): GeneralMeetingMinutes {
  return {
    type: DocumentType.general_meeting,
    generalMeetingType: GeneralMeetingType.regular,
    companyHasMultipleStockHolders: form.companyHasMultipleStockHolders,
    votes: form.votes.toJSON(),
    repairTextDeadlineGeneralMeeting: form.repairTextDeadlineGeneralMeeting,
    companySoleStockholder: form.companySoleStockholder.validatedValue,
    shareholderParticipation: form.shareholderRepresentation,
    documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
    attachments: Array.from(form.attachments.values()),
    additionalDetailsTexts: form.additionalDetailsTextModel.localizedTexts,
  };
}

export function updateShareholderParticipation(
  numberOfPresentShares: Record<string, number>,
  shareTypes: ShareType[],
  shareSumWithoutCompany: Record<string, number>,
): ShareholderParticipation[] {
  return shareTypes
    .filter((type) => shareSumWithoutCompany[type.sharetypeCode])
    .map((type) => {
      const sharesParticipating = numberOfPresentShares[type.sharetypeCode] ?? 0;
      const votesParticipating = sharesParticipating * type.vote;
      const totalShares = shareSumWithoutCompany[type.sharetypeCode] ?? 0;
      const percentage = Math.floor(((100 * sharesParticipating) / totalShares) * 100) / 100 || 0;
      return {
        shareTypeName: type.sharetypeCode,
        sharesParticipating: sharesParticipating,
        totalShares: totalShares,
        percentage: percentage,
        votesParticipating: votesParticipating,
      };
    });
}

export type NewSigner = {
  companyroleId: undefined;
  inputValue: string;
};

export type SignerOption = NewSigner | CompanyRoleIdWithName | undefined;

export const trimWhitespace = (s: string) => s.trim().replace(/ +/g, " ");

export function withCreateNewOption(options: SignerOption[], inputValue: string): SignerOption[] {
  if (
    inputValue &&
    options.every(
      (x) =>
        x?.companyroleId && x?.name.toLocaleLowerCase().trim() !== inputValue.toLocaleLowerCase(),
    )
  ) {
    return [...options, { companyroleId: undefined, inputValue }];
  }
  return [...options, { companyroleId: undefined, inputValue: "" }];
}

export function calcTotalParticipation(participation: ShareholderParticipation[]): number {
  return sumBy(participation, "sharesParticipating") / (sumBy(participation, "totalShares") || 1);
}

export function meetingChairAndSignerLabel(form: GeneralMeetingMinutesForm): TMessage {
  return {
    translated: compact([
      form.context.generalMeetingFields.meetingChair.value?.name,
      form.context.generalMeetingFields.coSigner?.value?.name,
    ]).join(", "),
  };
}

export enum GeneralMeetingCustomPanel {
  meetingDetails = "meetingDetails",
  participation = "participation",
  chairAndSigner = "chairAndSigner",
  approvalOfAgenda = "approvalOfAgenda",
}

export function getGeneralMeetingCustomPanels(form: GeneralMeetingMinutesForm): DetailsPanel[] {
  return [
    {
      type: "custom",
      id: GeneralMeetingCustomPanel.meetingDetails,
      documentId: form.documentId,
      icon: "calendar",
      heading: { key: "dbot.meeting-details" },
      label: {
        translated: formatDateAndTime(
          form.locale,
          form.context.generalMeetingFields.dateGeneralMeeting.validatedValue,
          form.context.generalMeetingFields.timeGeneralMeeting.validatedValue,
        ),
      },
    },
    {
      type: "custom",
      id: GeneralMeetingCustomPanel.participation,
      documentId: form.documentId,
      icon: "users",
      heading: { key: "dbot.participation" },
      label: !form.companyHasMultipleStockHolders
        ? { translated: form.companySoleStockholder.validatedValue }
        : form.shareholderRepresentation.length === 0
          ? { key: "not-specified" }
          : {
              translated: formatPercent(
                form.locale,
                calcTotalParticipation(form.shareholderRepresentation),
                {
                  minimumFractionDigits: 0,
                },
              ),
            },
    },
  ];
}

export function getGeneralMeetingCustomAgendaItems(
  form: GeneralMeetingMinutesForm,
): DetailsPanel[] {
  return [
    {
      type: "custom",
      id: GeneralMeetingCustomPanel.chairAndSigner,
      documentId: form.documentId,
      icon: "signature",
      heading: { key: "dbot.general-meeting.chair-and-signer" },
      label: meetingChairAndSignerLabel(form),
      hasValidationError: form.validateMessageError.length > 0,
    },
    {
      type: "custom",
      id: GeneralMeetingCustomPanel.approvalOfAgenda,
      documentId: form.documentId,
      icon: "list-bullets",
      heading: { key: "documents.generalMeeting.approveAgenda" },
    },
  ];
}

export function createGeneralMeetingVotingModel(
  context: ProjectContext,
  data: GeneralMeetingMinutes,
  defaultVoting: GeneralMeetingVoting = unanimous(),
) {
  const votes = data?.votes ?? defaultVotes();
  context.currentDecisions
    .filter((d) => !votes.decisions.some((dd) => d.decisionId === dd.decisionId))
    .forEach((d) =>
      votes.decisions.push({
        decisionId: d.decisionId,
        voting: defaultVoting,
      }),
    );

  const votingInput: VotingInputData = {
    shareholderData: context.shareholderData,
    shareNominalValue:
      context.inputStates[0]?.sharenominalValue ?? context.companyInfo.shareNominalValue,
  };

  return new GeneralMeetingVotesModel(votes, votingInput);
}

export function activeShareholdersEligibleForVoting(
  shareholders: ExtendedShareholderDTO[],
  sharetypes: ShareType[],
) {
  const sharetypesWithVoting = sharetypes.filter((s) => s.vote > 0).map((s) => s.sharetypeCode);
  return shareholders.filter((s) =>
    Object.entries(s.calculatedShares).some(
      ([key, value]) => value > 0 && sharetypesWithVoting.includes(key as ShareTypeCode),
    ),
  );
}

export function mapToParticipationData({
  orgNo,
  participationList,
  poaMap,
  shareholderData,
  roles,
  defaultPresentValue,
  shareholderParticipation,
}: {
  orgNo: string;
  participationList: GeneralMeetingParticipation[];
  poaMap: Map<number, PowerOfAttorneyForGeneralMeeting>;
  shareholderData: ShareholderData;
  roles: CompanyRole[];
  defaultPresentValue: boolean;
  shareholderParticipation: ShareholderParticipation[];
}) {
  const activeShareholders = shareholderData.shareholders
    .filter(hasShares)
    .filter((s) => s.birthDateOrOrganisationNumber !== orgNo);

  const existingParticipationWithShares =
    participationList?.filter((participant) =>
      activeShareholders.some(
        (activeShareholder) => participant.shareholderId === activeShareholder.id,
      ),
    ) ?? [];
  const representShareholders = new Map<number, RepresentativeOption>();

  const existingParticipation = new Map(
    existingParticipationWithShares.map((p) => [p.shareholderId, p]),
  );

  existingParticipation.forEach((participation) => {
    if (poaMap.has(participation.shareholderId)) {
      const poa = poaMap.get(participation.shareholderId)!;
      const companyRoleId = getCompanyRoleIdForRole(roles, poa);
      representShareholders.set(participation.shareholderId, {
        name: poa.poaRecipientName,
        companyRoleId: companyRoleId,
      });
    } else if (participation.representedByName) {
      representShareholders.set(participation.shareholderId, {
        name: participation.representedByName,
        companyRoleId: participation.representedById,
      });
    }
  });

  const newParticipation = new Map<number, GeneralMeetingParticipation>();
  // if no representative is set, check if a power of attorney is given and use that to prefill
  activeShareholders.forEach((shareholder) => {
    if (!existingParticipation.has(shareholder.id)) {
      if (poaMap.has(shareholder.id)) {
        const poa = poaMap.get(shareholder.id)!;
        const companyRoleId = getCompanyRoleIdForRole(roles, poa);
        representShareholders.set(shareholder.id, {
          name: poa.poaRecipientName,
          companyRoleId: companyRoleId,
        });

        newParticipation.set(shareholder.id, {
          representedByName: poa.poaRecipientName,
          representedById: companyRoleId,
          shareholderId: shareholder.id,
          participationId: newUuid(),
          isPresent: defaultPresentValue,
        });
      } else {
        newParticipation.set(shareholder.id, {
          shareholderId: shareholder.id,
          participationId: newUuid(),
          isPresent: defaultPresentValue,
        });
      }
    }
  });
  const presentShareholders = activeShareholders.filter((activeShareholder) =>
    participationList.some(
      (participant) => participant.shareholderId === activeShareholder.id && participant.isPresent,
    ),
  );

  const shareholderRepresentation =
    existingParticipation.size > 0
      ? updateShareholderParticipation(
          numberOfSharesForShareholders(presentShareholders),
          shareholderData.shareTypes,
          shareholderData.shareSumWithoutCompany,
        )
      : shareholderParticipation;
  return {
    existingParticipation,
    newParticipation,
    representShareholders,
    shareholderRepresentation,
  };
}

function getCompanyRoleIdForRole(
  roles: CompanyRole[],
  powerOfAttorney: PowerOfAttorneyForGeneralMeeting,
) {
  switch (powerOfAttorney.poaRecipientRole) {
    case "chairperson":
      return getChairperson(roles)?.companyroleId;
    case "ceo":
      return getCEO(roles)?.companyroleId;
    default:
      return undefined;
  }
}
